import _ from 'lodash';
import { Component, OnInit, Input, DestroyRef, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CampaignService } from '../../services/campaign.service';
import { select, Store } from '@ngrx/store';
import { loadEventList } from 'app/event/features/event.actions';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { selectEventList } from 'app/event/features/event.selectors';
import { MDBSwitchService } from 'app/core/services/mdb-switch.service';
import { selectCampaignSelected } from 'app/campaign/features/campaign.selectors';

@Component({
  selector: 'app-campaign-ticketing-type',
  templateUrl: './campaign-ticketing-type.component.html',
  styleUrls: ['./campaign-ticketing-type.component.scss']
})
export class CampaignTicketingTypeComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() formGroupCampaignTypeData: UntypedFormGroup = null;

  private _campaignId: any = null;

  @Input()
  set campaignId(id: any) {
    this._campaignId = id;
  };
  get campaignId(): any {
    return this._campaignId;
  }

  public soldCount: number = 0;

  public campaign$ = this.store.pipe(
    select(selectCampaignSelected),
    filter(campaign => campaign !== null)
  );

  public events$ = this.store.pipe(
    select(selectEventList),
    filter(events => events !== null)
  );

  public selectedEventType = null;
  public selectedEventName = null;

  public ticketForList: any[] = [{
    value: 1,
    label: 'Virtual Event'
  }, {
    value: 2,
    label: 'In-Person Event'
  }]

  constructor(
    public store: Store,
    public campaignService: CampaignService,
    public mdbSwitchService: MDBSwitchService,
    private sharedService: SharedService,
  ) {
    this.store.dispatch({
      type: loadEventList.type,
      payload: this.sharedService.token
    });
  }

  ngOnInit(): void {
    combineLatest([
      this.campaign$,
      this.events$
    ]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([campaign, eventList]) => {
      if (this.formGroupCampaignTypeData) {
        this.initializeFormControls(campaign);
        this.handleEventSelection(eventList);
      }
    });

    const isRaffleTicket = this.formGroupCampaignTypeData.get('isRaffleTicket') as UntypedFormControl;
    const isTicketForInPersonEvent = this.formGroupCampaignTypeData.get('isTicketForInPersonEvent') as UntypedFormControl;
    const isTicketForVirtualEvent = this.formGroupCampaignTypeData.get('isTicketForVirtualEvent') as UntypedFormControl;
    const linkedEventId = this.formGroupCampaignTypeData.get('linkedEventId') as UntypedFormControl;
    const saveCardEnabled = this.formGroupCampaignTypeData.get('saveCardEnabled') as UntypedFormControl;

    // Setup form control subscriptions
    isRaffleTicket.valueChanges.pipe(
      filter(value => value),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.disableIsTicketForInPersonEvent();
      this.disableIsTicketForVirtualEvent();
      this.enableIsTicketForRaffle();
    });

    isRaffleTicket.valueChanges.pipe(
      filter(value => !value),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.disableIsTicketForRaffle();
    });

    isTicketForInPersonEvent.valueChanges.pipe(
      filter(value => value),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      this.disableIsTicketForRaffle();
      this.disableIsTicketForVirtualEvent();
      this.enableIsTicketForInPersonEvent();

      if(linkedEventId.value) {
        saveCardEnabled.enable();
      } else {
        saveCardEnabled.disable();
        saveCardEnabled.setValue(false);
      }
    });

    isTicketForInPersonEvent.valueChanges.pipe(
      filter(value => !value),
      filter(value => value === isRaffleTicket.value),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.disableIsTicketForInPersonEvent();
    });

    isTicketForVirtualEvent.valueChanges.pipe(
      filter(value => value),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      this.disableIsTicketForRaffle();
      this.disableIsTicketForInPersonEvent();
      this.enableIsTicketForVirtualEvent();
    });

    isTicketForVirtualEvent.valueChanges.pipe(
      filter(value => !value),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      this.disableIsTicketForVirtualEvent();
    });
  }

  private handleEventSelection(list: any[]): void {
    this.selectedEventType = null;
    const linkedEventId = this.formGroupCampaignTypeData.get('linkedEventId') as UntypedFormControl;
    const linkedVirtualEventID = this.formGroupCampaignTypeData.get('linkedVirtualEventID') as UntypedFormControl;

    if(linkedEventId.value) {
      const event = _.find(list, element => {
        switch(element.type) {
          case 0:
            const { inPersonEventInfo } = element;
            if(inPersonEventInfo.id === linkedEventId.value) {
              this.selectedEventType = 0;
              return true;
            }
            break
          case 2:
            const { hybridEventInfo: { inPersonEvent } } = element;
            if(inPersonEvent.id === linkedEventId.value) {
              this.selectedEventType = 0;
              return true;
            }
            break
        }
        return false;
      }) || null;
      if(event) {
        this.selectedEventName = this.getEventName(event, 0);
      }
    }

    if(linkedVirtualEventID.value) {
      const event = _.find(list, element => {
        switch(element.type) {
          case 1:
            const { virtualEventInfo } = element;
            if(virtualEventInfo.id === linkedVirtualEventID.value) {
              this.selectedEventType = 1;
              return true;
            }
            break
          case 2:
            const { hybridEventInfo: { virtualEvent } } = element;
            if(virtualEvent.id === linkedVirtualEventID.value) {
              this.selectedEventType = 1;
              return true;
            }
            break
        }
        return false;
      }) || null;
      if(event) {
        this.selectedEventName = this.getEventName(event, 1);
      }
    }
  }

  private initializeFormControls(campaign): void {
    const isRaffleTicket = this.formGroupCampaignTypeData.get('isRaffleTicket') as UntypedFormControl;
    const linkedEventId = this.formGroupCampaignTypeData.get('linkedEventId') as UntypedFormControl;
    const linkedVirtualEventID = this.formGroupCampaignTypeData.get('linkedVirtualEventID') as UntypedFormControl;
    const isTicketForInPersonEvent = this.formGroupCampaignTypeData.get('isTicketForInPersonEvent') as UntypedFormControl;
    const isTicketForVirtualEvent = this.formGroupCampaignTypeData.get('isTicketForVirtualEvent') as UntypedFormControl;
    const saveCardEnabled = this.formGroupCampaignTypeData.get('saveCardEnabled') as UntypedFormControl;

    // Set initial states
    if(isTicketForVirtualEvent.value === true) {
      this.enableIsTicketForVirtualEvent();
    }

    if(isTicketForInPersonEvent.value === true) {
      this.enableIsTicketForInPersonEvent();
    }

    if(linkedEventId.value) {
      this.disableAllFlags();

      if(isTicketForInPersonEvent.value === false) {
        isTicketForInPersonEvent.setValue(true);
      }
      saveCardEnabled.enable();
      saveCardEnabled.setValue(campaign.campaignTypeData.saveCardEnabled);
    } else {
      saveCardEnabled.disable();
      saveCardEnabled.setValue(false);
    }

    if(linkedVirtualEventID.value) {
      this.disableAllFlags();

      if(isTicketForVirtualEvent.value === false) {
        isTicketForVirtualEvent.setValue(true);
      }
    }

    if(isRaffleTicket.value) {      
      this.disableIsTicketForInPersonEvent();
      this.disableIsTicketForVirtualEvent();
      this.enableIsTicketForRaffle();
    }
  }

  setControlValue(name, value) {
    const control = this.formGroupCampaignTypeData.get(name) as UntypedFormControl;
    control.setValue(value);
  }

  enableIsTicketForRaffle () {
    this.setControlValue("sendTickets", false);
    this.setControlValue("guestRequired", false);
    this.setControlValue("isTicketForInPersonEvent", false);
    this.setControlValue("isTicketForVirtualEvent", false);
    this.setControlValue("sendCheckinEmail", false);
    this.setControlValue("sendCheckinText", false);

    this.mdbSwitchService.disable("isTicketForInPersonEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("isTicketForVirtualEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("sendTickets", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("guestRequired", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("sendCheckinEmail", {
      emitEvent: false
    });
  }

  disableIsTicketForRaffle() {
    this.mdbSwitchService.enable("isTicketForInPersonEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("isTicketForVirtualEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("sendTickets", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("guestRequired", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("sendCheckinEmail", {
      emitEvent: false
    });
  }

  enableIsTicketForInPersonEvent() {
    this.setControlValue("guestRequired", true);
    this.setControlValue("isTicketForVirtualEvent", false);

    this.mdbSwitchService.disable("guestRequired", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("isTicketForVirtualEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("isRaffleTicket", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("basketRaffle", {
      emitEvent: false
    });
  }

  disableIsTicketForInPersonEvent() {
    this.mdbSwitchService.enable("guestRequired", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("isTicketForVirtualEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("isRaffleTicket", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("basketRaffle", {
      emitEvent: false
    });
  }

  enableIsTicketForVirtualEvent() {
    this.setControlValue("sendTickets", false);
    this.setControlValue("sendCheckinEmail", false);
    this.setControlValue("sendCheckinText", false);
    this.setControlValue("isTicketForInPersonEvent", false);

    this.mdbSwitchService.disable("sendTickets", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("sendCheckinEmail", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("isTicketForInPersonEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("isRaffleTicket", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("basketRaffle", {
      emitEvent: false
    });
  }

  disableIsTicketForVirtualEvent() {
    this.mdbSwitchService.enable("sendTickets", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("sendCheckinEmail", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("isTicketForInPersonEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("isRaffleTicket", {
      emitEvent: false
    });
    this.mdbSwitchService.enable("basketRaffle", {
      emitEvent: false
    });
  }

  disableAllFlags(): void {
    this.mdbSwitchService.disable("isTicketForInPersonEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("isTicketForVirtualEvent", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("basketRaffle", {
      emitEvent: false
    });
    this.mdbSwitchService.disable("isRaffleTicket", {
      emitEvent: false
    });
  }

  getEventName(event, type = null): void {
    switch(event.type) {
      case 0:
        const { inPersonEventInfo } = event;
        return inPersonEventInfo.name;
      case 1:
        const { virtualEventInfo } = event;
        return virtualEventInfo.name;
      case 2:
        const { hybridEventInfo } = event;
        switch(type) {
          case 0:
            const { inPersonEvent } = hybridEventInfo;
            return inPersonEvent.name;
          case 1:
            const { virtualEvent } = hybridEventInfo;
            return virtualEvent.name;
        }
    }
    return null;
  }

}
